import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { usePatchBrokersBrokerAttentionNeededItems } from 'utils/web_api'
import { type BrokersBroker } from 'utils/web_api.schemas'

import NAVAssignmentDropdown from './NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  setMissingPaymentsQueryDataCallback,
} from './utils'
import NeedsAttentionContext from '../../context/NeedsAttentionContext'
import { useGetBrokers } from '../../hooks/needs_attention/useGetBrokers'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from '../../reusable/table/ComboboxMultiSelectFilter'

type AssignTagsToNAVItemProps = {
  navItemId: string
  assignedBrokers: BrokersBroker[]
}

const AssignTagsToNAVItem = ({
  navItemId,
  assignedBrokers,
}: AssignTagsToNAVItemProps) => {
  const queryClient = useQueryClient()
  const { getBrokersMissingPaymentsQueryKey } = useContext(
    NeedsAttentionContext
  )
  const { currentBroker } = useContext(BrokerContext)
  const [query, setQuery] = useState('')

  const { data } = useGetBrokers()
  const brokers = data?.data

  const pinnedBrokerOptions: ComboboxFilterOption<string>[] = [
    ...(currentBroker
      ? [{ label: currentBroker?.broker_name, value: currentBroker?.id }]
      : []),
  ]

  const brokerOptions: ComboboxFilterOption<string>[] =
    brokers
      ?.map((broker) => ({
        label: broker.broker_name,
        value: broker.id,
      }))
      .filter((option) => option.value !== currentBroker?.id) || []

  const addAssigneesToNAVItemMutation = usePatchBrokersBrokerAttentionNeededItems()

  const assignedBrokerIds = assignedBrokers.map((broker) => broker.id)

  const handleSelectTag = (brokerId: string) => {
    addAssigneesToNAVItemMutation.mutate(
      {
        data: {
          broker_attention_needed_item_ids: [navItemId],
          ...(assignedBrokerIds.includes(brokerId)
            ? { broker_ids_to_remove: [brokerId] }
            : { broker_ids_to_add: [brokerId] }),
        },
      },
      {
        onSuccess: (response) => {
          // Update cached data with new NAV items that include updated tags.
          // TODO: Investigate looking at query params and removing item if not part of the current page.
          queryClient.setQueryData(
            getBrokersMissingPaymentsQueryKey,
            setMissingPaymentsQueryDataCallback(response)
          )
        },
      }
    )
  }

  const filteredPinnedBrokerOptions = pinnedBrokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )
  const filteredBrokerOptions = brokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedBrokerIds}
        options={[
          { groupLabel: 'Pinned', options: filteredPinnedBrokerOptions },
          { groupLabel: 'Brokers', options: filteredBrokerOptions },
        ]}
        onSelectValue={handleSelectTag}
        placeholder='Search brokers to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Assignee'
          pluralIdentifier='assignees'
          selectedValues={assignedBrokerIds}
          options={[...pinnedBrokerOptions, ...brokerOptions]}
          isPending={addAssigneesToNAVItemMutation.isPending}
          type='assignee'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignTagsToNAVItem
